// Research: the addresses a person types, and the competitor checklist read from the session. Plain TypeScript, no I/O.
import type { CompetitorList, SessionState } from "../domain/types";

/** The server allows at most this many rows on the competitor checklist (typed and suggested together). */
export const MAX_LISTED_COMPETITORS = 10;

/** The longest web address accepted; the server enforces the same limit. */
export const MAX_ADDRESS_LENGTH = 2048;

const WEB_PROTOCOLS = ["http:", "https:"];
/** A scheme at the very start, such as "https://". A "://" later in the address (a query string, say) is not one. */
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const WHITESPACE_OR_CONTROL = /[\s\p{Cc}]/u;

/** True for an http or https web address. Research output is never trusted as a link, so anything else is shown as text. */
export function isWebAddress(text: string): boolean {
  try {
    return WEB_PROTOCOLS.includes(new URL(text).protocol);
  } catch {
    return false; // not an address at all, such as a plain sentence or "https://"
  }
}

/**
 * What a person typed as a web address, trimmed, with https added when no scheme was typed (the server does the same). Null when it is not
 * a web address, has login details, holds whitespace or control characters, or is longer than MAX_ADDRESS_LENGTH once https is added.
 */
export function webAddress(text: string): string | null {
  const typed = text.trim();
  if (!typed || WHITESPACE_OR_CONTROL.test(typed)) return null;
  const address = SCHEME.test(typed) ? typed : `https://${typed}`;
  if (address.length > MAX_ADDRESS_LENGTH) return null;
  try {
    const parsed = new URL(address);
    return WEB_PROTOCOLS.includes(parsed.protocol) && !parsed.username && !parsed.password ? address : null;
  } catch {
    return null;
  }
}

/** The Research checklist saved in the session: empty when nothing was ever listed. */
export function competitorList(state: SessionState | undefined): CompetitorList {
  return state?.competitors ?? { listed: [], removed: [] };
}

/** How many rows of the checklist are ticked, that is, go to the Value drivers step. */
export function tickedCount(list: CompetitorList): number {
  return list.listed.filter((row) => row.ticked).length;
}